import moment from "moment";
import React, { useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { HiOutlineChartBar } from "react-icons/hi";
import { Spinner } from "../../atoms/Spinner";
import { EmptyState } from "../../atoms/EmptyState";
import useAuth from "../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../../atoms/CustomButton";
import { useGetStatistic } from "../../../hooks/entity";

const COULEURS_GRAPHIQUE = {
  // design-ok: jeton blue-500, prop SVG de Recharts, pas de classe possible
  serieUtilisateurs: "#3B82F6",
  // design-ok: jeton green-500, prop SVG de Recharts, pas de classe possible
  serieConnexions: "#22C55E",
  // design-ok: jeton gray-200, prop SVG de Recharts, pas de classe possible
  grille: "#E5E7EB",
  // design-ok: jeton gray-500, prop SVG de Recharts, pas de classe possible
  graduation: "#6B7280",
  // design-ok: jeton gray-700, prop SVG de Recharts, pas de classe possible
  libelleAxe: "#374151",
  // design-ok: jeton gray-800, prop SVG de Recharts, pas de classe possible
  fondInfobulle: "#1F2937",
  // design-ok: jeton gray-50, prop SVG de Recharts, pas de classe possible
  texteInfobulle: "#F9FAFB",
  // design-ok: blanc pur, coeur du point survole, doit trancher sur la courbe
  coeurPoint: "#FFFFFF",
};

const UsersStatsChart = () => {
  const { t } = useTranslation();
  const { customer_identification, user } = useAuth();

  const [itt, setItt] = useState(7);

  const {
    isLoading,
    isError,
    data: res,
    refetch,
  } = useGetStatistic(customer_identification, user.entity_identification, itt);

  const handleFetchStats = (itt: number) => {
    setItt(itt);
  };

  const chartData =
    res && res[0]?.user_creation_date_time
      ? (() => {
          const creationData = res[0].user_creation_date_time;
          const connectionData = res[0]?.user_last_connection || {};

          const allDates = new Set([
            ...Object.keys(creationData),
            ...Object.keys(connectionData),
          ]);

          return Array.from(allDates)
            .map((date) => ({
              name: moment(date, "DD/MM/YYYY").format("YYYY/MM/DD"),
              users: ((creationData[date] as any[]) || []).length,
              connections: ((connectionData[date] as any[]) || []).length,
              displayDate: moment(date, "DD/MM/YYYY").format("MM/DD"),
            }))
            .sort((a, b) => moment(a.name).diff(b.name));
        })()
      : [];

  const renderChart = () => {
    if (!chartData.length) {
      return (
        <EmptyState
          className="h-64"
          icon={<HiOutlineChartBar size={24} />}
          title={t("dashboard.usersStatsChart.noData")}
          description={t("dashboard.usersStatsChart.noDataDesc")}
        />
      );
    }

    return (
      <div className="relative h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData.map((item) => ({
              ...item,
              name: item.displayDate,
            }))}
            margin={{
              top: 20,
              right: 5,
              left: 0,
              bottom: 10,
            }}
          >
            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={COULEURS_GRAPHIQUE.serieUtilisateurs}
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor={COULEURS_GRAPHIQUE.serieUtilisateurs}
                  stopOpacity={0}
                />
              </linearGradient>
              <linearGradient id="colorConnection" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={COULEURS_GRAPHIQUE.serieConnexions}
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor={COULEURS_GRAPHIQUE.serieConnexions}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={COULEURS_GRAPHIQUE.grille}
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: COULEURS_GRAPHIQUE.graduation }}
            >
              <Label
                value={t("dashboard.usersStatsChart.key3").toString()}
                position="insideBottom"
                offset={-10}
                style={{
                  textAnchor: "middle",
                  fontSize: "12px",
                  fontWeight: "500",
                  fill: COULEURS_GRAPHIQUE.libelleAxe,
                }}
              />
            </XAxis>
            <YAxis
              axisLine={false}
              tickLine={false}
              domain={[0, "dataMax + 1"]}
              allowDecimals={false}
              tick={{ fontSize: 12, fill: COULEURS_GRAPHIQUE.graduation }}
            >
              <Label
                value={t("dashboard.usersStatsChart.key4").toString()}
                angle={-90}
                position="insideLeft"
                style={{
                  textAnchor: "middle",
                  fontSize: "12px",
                  fontWeight: "500",
                  fill: COULEURS_GRAPHIQUE.libelleAxe,
                }}
              />
            </YAxis>
            <Tooltip
              contentStyle={{
                backgroundColor: COULEURS_GRAPHIQUE.fondInfobulle,
                color: COULEURS_GRAPHIQUE.texteInfobulle,
                border: "none",
                borderRadius: "8px",
                boxShadow:
                  "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              }}
              labelStyle={{ color: COULEURS_GRAPHIQUE.texteInfobulle }}
              formatter={(value: any, name: string) => {
                const label =
                  name === "users"
                    ? t("dashboard.usersStatsChart.key2")
                    : t("dashboard.usersStatsChart.key5");
                return [value, label];
              }}
            />
            <Area
              type="monotone"
              dataKey="users"
              stroke={COULEURS_GRAPHIQUE.serieUtilisateurs}
              strokeWidth={2}
              fill="url(#colorUv)"
              dot={{
                fill: COULEURS_GRAPHIQUE.serieUtilisateurs,
                strokeWidth: 2,
                r: 4,
              }}
              activeDot={{
                r: 6,
                stroke: COULEURS_GRAPHIQUE.serieUtilisateurs,
                strokeWidth: 2,
                fill: COULEURS_GRAPHIQUE.coeurPoint,
              }}
            />
            <Area
              type="monotone"
              dataKey="connections"
              stroke={COULEURS_GRAPHIQUE.serieConnexions}
              strokeWidth={2}
              fill="url(#colorConnection)"
              dot={{
                fill: COULEURS_GRAPHIQUE.serieConnexions,
                strokeWidth: 2,
                r: 4,
              }}
              activeDot={{
                r: 6,
                stroke: COULEURS_GRAPHIQUE.serieConnexions,
                strokeWidth: 2,
                fill: COULEURS_GRAPHIQUE.coeurPoint,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("dashboard.usersStatsChart.title")}
        </h2>
        <p className="mt-0.5 text-sm text-gray-500">
          {t("dashboard.usersStatsChart.introduction")}
        </p>
      </div>
      <div className="w-full bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-6">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <div className="text-sm font-medium text-gray-700">
                {t("dashboard.usersStatsChart.timePeriod")}
              </div>
              <div className="flex gap-2 flex-wrap">
                {[7, 30, 90].map((period: number) => (
                  <CustomButton
                    key={period}
                    size="sm"
                    variant={period === itt ? "primary" : "secondary"}
                    onClick={() => handleFetchStats(period)}
                    disabled={isLoading}
                    isLoading={isLoading && period === itt}
                    title={t("dashboard.usersStatsChart.key1", { period })}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full" />
                <span className="text-sm text-gray-700 font-medium">
                  {t("dashboard.usersStatsChart.key2")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
                <span className="text-sm text-gray-700 font-medium">
                  {t("dashboard.usersStatsChart.key5")}
                </span>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center gap-3">
                  <Spinner size="lg" />
                  <span className="text-sm text-gray-600">
                    {t("dashboard.usersStatsChart.loading")}
                  </span>
                </div>
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="text-center">
                  <div className="text-lg font-medium text-gray-900 mb-2">
                    {t("dashboard.usersStatsChart.error")}
                  </div>
                  <div className="text-sm text-gray-600 mb-4">
                    {t("dashboard.usersStatsChart.errorDesc")}
                  </div>
                </div>
                <CustomButton
                  onClick={() => refetch()}
                  variant="primary"
                  title={t("dashboard.usersStatsChart.btn-01") as string}
                />
              </div>
            ) : (
              renderChart()
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UsersStatsChart;
